import { supabase } from '../utils/supabaseClient';
import { companyWallClockToUtc } from '../utils/dateFormatters';

const nextDayKey = (key) => {
  const [y, m, d] = String(key).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + 1));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};

/**
 * Service for generating adjustments reports and analytics
 * Provides insights into adjustments by type, reason, user, and date range
 */
class AdjustmentsReportingService {
  /**
   * Fetch adjustments summary for a date range
   * @param {string} companyId - Company ID
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} Summary of adjustments
   */
  async getAdjustmentsSummary(companyId, startDate, endDate) {
    try {
      // !inner makes the company filter actually filter rows; without it PostgREST only nulls
      // the embed, and the report listed another company's adjustments (board #4151 audit).
      const { data: adjustments, error } = await supabase
        .from('work_order_adjustments')
        .select('*, work_orders!inner(company_id)')
        // Company-day bounds (#4358): the bare 'T00:00:00' strings were read as UTC, so an
        // evening adjustment in New York landed in the next day's range.
        .gte('created_at', companyWallClockToUtc(startDate, '00:00'))
        .lt('created_at', companyWallClockToUtc(nextDayKey(endDate), '00:00'))
        .eq('work_orders.company_id', companyId);

      if (error) {
        console.error('Error fetching adjustments:', error);
        return this.getEmptySummary();
      }

      const userNames = await this.resolveUserNames(adjustments || []);
      return this.calculateSummary(adjustments || [], userNames);
    } catch (error) {
      console.error('Exception in getAdjustmentsSummary:', error);
      return this.getEmptySummary();
    }
  }

  async resolveUserNames(adjustments) {
    const ids = [...new Set(adjustments.map(a => a.created_by).filter(Boolean))];
    if (ids.length === 0) return {};
    const { data } = await supabase.from('users').select('id, first_name, last_name, name, email').in('id', ids);
    return Object.fromEntries((data || []).map(u => [
      u.id,
      [u.first_name, u.last_name].filter(Boolean).join(' ') || u.name || u.email || 'Unknown'
    ]));
  }

  /**
   * Live model (verified 2026-10-07): adjustment_type is FIXED | PERCENTAGE, and a separate
   * direction column is CREDIT (reduces the price) | DEBIT (adds to it). The old code summed
   * types 'FIXED_AMOUNT' and 'CREDIT', which never occur, so Total Amount was always $0 next to
   * real $20 rows (board #4151/#4351/#4152).
   *
   * totalAmount = net of FIXED adjustments, DEBIT positive and CREDIT negative. PERCENTAGE rows
   * are counted but not summed — their dollar value depends on the job they were applied to.
   */
  calculateSummary(adjustments, userNames = {}) {
    const summary = this.getEmptySummary();
    summary.totalCount = adjustments.length;
    let fixedCount = 0;

    adjustments.forEach(adj => {
      const type = adj.adjustment_type === 'PERCENTAGE' ? 'PERCENTAGE' : 'FIXED';
      const direction = adj.direction === 'CREDIT' ? 'CREDIT' : 'DEBIT';
      summary.byType[type] += 1;
      summary.byDirection[direction] += 1;

      const reason = adj.reason || 'No Reason';
      summary.byReason[reason] = (summary.byReason[reason] || 0) + 1;

      const user = (adj.created_by && userNames[adj.created_by]) || 'Unknown';
      summary.byUser[user] = (summary.byUser[user] || 0) + 1;

      const amount = Number(adj.amount || 0);
      const signed = direction === 'CREDIT' ? -amount : amount;
      if (type === 'FIXED') {
        summary.totalAmount += signed;
        fixedCount += 1;
      }

      summary.breakdown.push({
        id: adj.id,
        type,
        direction,
        amount: type === 'FIXED' ? signed : null,
        percentage: adj.percentage != null ? Number(adj.percentage) : null,
        reason: adj.reason,
        createdBy: user,
        createdAt: adj.created_at
      });
    });

    summary.averageAmount = fixedCount > 0 ? summary.totalAmount / fixedCount : 0;
    return summary;
  }

  /**
   * Get adjustments by type with detailed breakdown
   */
  async getAdjustmentsByType(companyId, startDate, endDate) {
    const summary = await this.getAdjustmentsSummary(companyId, startDate, endDate);
    
    return {
      FIXED: summary.byType.FIXED || 0,
      PERCENTAGE: summary.byType.PERCENTAGE || 0,
      total: summary.totalCount
    };
  }

  /**
   * Get adjustments by reason
   */
  async getAdjustmentsByReason(companyId, startDate, endDate) {
    const summary = await this.getAdjustmentsSummary(companyId, startDate, endDate);
    return summary.byReason;
  }

  /**
   * Get adjustments by user
   */
  async getAdjustmentsByUser(companyId, startDate, endDate) {
    const summary = await this.getAdjustmentsSummary(companyId, startDate, endDate);
    return summary.byUser;
  }

  /**
   * Get empty summary object
   */
  getEmptySummary() {
    return {
      totalCount: 0,
      byType: { FIXED: 0, PERCENTAGE: 0 },
      byDirection: { CREDIT: 0, DEBIT: 0 },
      byReason: {},
      byUser: {},
      totalAmount: 0,
      averageAmount: 0,
      breakdown: []
    };
  }
}

export default new AdjustmentsReportingService();

